import { apiRequest } from './core/client';
import type { AiAnnotation } from '@/types/ai';

interface AiTaskResponse {
  taskId: string;
  annotation: AiAnnotation | null;
  error?: string;
}

interface MessageSummaryParams {
  accountId: string;
  messageId: string;
}

interface RunMessageSummaryParams extends MessageSummaryParams {
  force?: boolean;
  source?: string;
}

type MessageTaskSuggestionParams = MessageSummaryParams;
type RunMessageTaskSuggestionParams = RunMessageSummaryParams;

export async function fetchMessageSummary(params: MessageSummaryParams): Promise<AiAnnotation | null> {
  const { accountId, messageId } = params;
  const response = await apiRequest<AiTaskResponse>({
    path: `/ai/messages/${encodeURIComponent(messageId)}/summarize`,
    method: 'GET',
    accountId,
  });
  return response.annotation ?? null;
}

export async function runMessageSummary(params: RunMessageSummaryParams): Promise<AiAnnotation> {
  const { accountId, messageId, force = false, source } = params;
  const body: Record<string, unknown> = {};
  if (force) {
    body.force = true;
  }
  if (source) {
    body.source = source;
  }
  const response = await apiRequest<AiTaskResponse>({
    path: `/ai/messages/${encodeURIComponent(messageId)}/summarize`,
    method: 'POST',
    accountId,
    body,
  });
  if (!response.annotation) {
    throw new Error(response.error || 'AI summary unavailable');
  }
  return response.annotation;
}

export async function fetchMessageTaskSuggestions(params: MessageTaskSuggestionParams): Promise<AiAnnotation | null> {
  const { accountId, messageId } = params;
  const response = await apiRequest<AiTaskResponse>({
    path: `/ai/messages/${encodeURIComponent(messageId)}/task-suggestions`,
    method: 'GET',
    accountId,
  });
  return response.annotation ?? null;
}

export async function runMessageTaskSuggestions(params: RunMessageTaskSuggestionParams): Promise<AiAnnotation> {
  const { accountId, messageId, force = false, source } = params;
  const body: Record<string, unknown> = {};
  if (force) {
    body.force = true;
  }
  if (source) {
    body.source = source;
  }
  const response = await apiRequest<AiTaskResponse>({
    path: `/ai/messages/${encodeURIComponent(messageId)}/task-suggestions`,
    method: 'POST',
    accountId,
    body,
  });
  if (!response.annotation) {
    throw new Error(response.error || 'AI task suggestions unavailable');
  }
  return response.annotation;
}

type MessageEventSuggestionParams = MessageSummaryParams;
type RunMessageEventSuggestionParams = RunMessageSummaryParams;

export async function fetchMessageEventSuggestions(params: MessageEventSuggestionParams): Promise<AiAnnotation | null> {
  const { accountId, messageId } = params;
  const response = await apiRequest<AiTaskResponse>({
    path: `/ai/messages/${encodeURIComponent(messageId)}/event-suggestions`,
    method: 'GET',
    accountId,
  });
  return response.annotation ?? null;
}

export async function runMessageEventSuggestions(params: RunMessageEventSuggestionParams): Promise<AiAnnotation> {
  const { accountId, messageId, force = false, source } = params;
  const body: Record<string, unknown> = {};
  if (force) {
    body.force = true;
  }
  if (source) {
    body.source = source;
  }
  const response = await apiRequest<AiTaskResponse>({
    path: `/ai/messages/${encodeURIComponent(messageId)}/event-suggestions`,
    method: 'POST',
    accountId,
    body,
  });
  if (!response.annotation) {
    throw new Error(response.error || 'AI event suggestions unavailable');
  }
  return response.annotation;
}

type ProductLineExtractionParams = MessageSummaryParams;
type RunProductLineExtractionParams = RunMessageSummaryParams;

export async function fetchProductLineExtraction(params: ProductLineExtractionParams): Promise<AiAnnotation | null> {
  const { accountId, messageId } = params;
  const response = await apiRequest<AiTaskResponse>({
    path: `/ai/messages/${encodeURIComponent(messageId)}/product-lines`,
    method: 'GET',
    accountId,
  });
  return response.annotation ?? null;
}

export async function runProductLineExtraction(params: RunProductLineExtractionParams): Promise<AiAnnotation> {
  const { accountId, messageId, force = false, source } = params;
  const body: Record<string, unknown> = {};
  if (force) {
    body.force = true;
  }
  if (source) {
    body.source = source;
  }
  const response = await apiRequest<AiTaskResponse>({
    path: `/ai/messages/${encodeURIComponent(messageId)}/product-lines`,
    method: 'POST',
    accountId,
    body,
  });
  if (!response.annotation) {
    throw new Error(response.error || 'Product line extraction unavailable');
  }
  return response.annotation;
}
